import {
  Box,
  Button,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  HStack,
  Icon,
  Image,
  Input,
  InputGroup,
  InputLeftElement,
  Link,
  Spacer,
  Text,
} from '@chakra-ui/react'
import { AppContainer } from 'components/ui/appContainer'
import { CustomHeading } from 'components/ui/customHeading'
import { NextLink } from 'components/wrapper/nextLink'
import { SignInParams, useAuthDispatcher } from 'context/auth'
import { useMyselfState } from 'context/myself'
import { publicEnv } from 'env'
import { useShowErrorMessageIfNeed } from 'hooks/useShowErrorMessageIfNeed'
import { NextPage } from 'next'
import { NextSeo } from 'next-seo'
import { useRouter } from 'next/dist/client/router'
import { useCallback, useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { MdPerson, MdVpnKey } from 'react-icons/md'
import { Routes } from 'routes'

type FormInput = SignInParams

const Page: NextPage = () => {
  const [submitting, setSubmitting] = useState(false)
  const { signIn } = useAuthDispatcher()
  const { myself } = useMyselfState()
  const { showErrorMessageIfNeed } = useShowErrorMessageIfNeed()
  const router = useRouter()
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormInput>({ mode: 'onSubmit' })

  const onSubmit = useCallback(
    async (data: FormInput) => {
      setSubmitting(true)
      try {
        await signIn({ email: data.email, password: data.password })
      } catch (e) {
        showErrorMessageIfNeed(e)
        setSubmitting(false)
      }
    },
    [signIn]
  )

  useEffect(() => {
    if (!myself) return
    if (myself.employee) {
      router.push(Routes.searchHotelsTop())
      return
    }

    if (!myself.latestAgencyRegistration) {
      router.push(Routes.createAgencyRequests())
      return
    }

    router.push(Routes.myPage())
  }, [myself])

  return (
    <>
      <NextSeo title="ログイン | HoteCo" openGraph={{ url: `${publicEnv.host}${Routes.signIn()}` }} />
      <AppContainer>
        <Box pt={12} pb={'100px'}>
          <CustomHeading as="h2">ログイン</CustomHeading>

          <Flex mt={3}>
            <Flex pt={'30px'}>
              <Box w="70px" h="full" boxShadow="0px 1px 4px 0px #00000040" rounded="5px" position="relative" zIndex={1}>
                <Image src="/assets/pages/signin/side.png" w="full" h="full" />
              </Box>

              <Box background="#FCFCFCE5" boxShadow="0px 1px 4px 0px #00000040" pl="52px" pr="90px" pt={9} rounded="md">
                <form onSubmit={handleSubmit(onSubmit)}>
                  <Flex direction="column" w="346px">
                    <FormControl isInvalid={!!errors.email}>
                      <FormLabel>メールアドレス</FormLabel>
                      <InputGroup>
                        {/* eslint-disable-next-line react/no-children-prop */}
                        <InputLeftElement children={<Icon as={MdPerson} w={6} h={6} color="blue.600" />} />
                        <Input
                          {...register('email', { required: 'メールアドレスの入力は必須です' })}
                          type="email"
                          placeholder="example@example.co.jp"
                        />
                      </InputGroup>
                      <FormErrorMessage>{errors.email?.message}</FormErrorMessage>
                    </FormControl>

                    <Spacer mt={6} />

                    <FormControl isInvalid={!!errors.password}>
                      <FormLabel>パスワード</FormLabel>
                      <InputGroup>
                        {/* eslint-disable-next-line react/no-children-prop */}
                        <InputLeftElement children={<Icon as={MdVpnKey} w={6} h={6} color="blue.600" />} />
                        <Input
                          {...register('password', { required: 'パスワードの入力は必須です' })}
                          type="password"
                          autoComplete="current-password"
                        />
                      </InputGroup>
                      <FormErrorMessage>{errors.password?.message}</FormErrorMessage>
                    </FormControl>

                    <Spacer mt={4} />

                    <Link as={NextLink} href={Routes.resetPassword()}>
                      <HStack spacing={2}>
                        <Image src="/assets/arrow_right_circle.svg" boxSize={4} />
                        <Text fontSize={14} lineHeight={1}>
                          パスワードを忘れた場合はこちら
                        </Text>
                      </HStack>
                    </Link>

                    <Spacer mt={7} />

                    <Button type="submit" isLoading={submitting}>
                      ログイン
                    </Button>
                  </Flex>
                </form>

                <Spacer mt={4} />

                <Link as={NextLink} href={Routes.signUp()}>
                  <HStack spacing={2}>
                    <Image src="/assets/arrow_right_circle.svg" boxSize={4} />
                    <Text fontSize={14} lineHeight={1}>
                      新規会員登録
                    </Text>
                  </HStack>
                </Link>
              </Box>
            </Flex>

            <Image src="/assets/pages/signin/main.svg" w="600px" h="430px" ml="auto" />
          </Flex>
        </Box>
      </AppContainer>
    </>
  )
}

export default Page
